import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNotificationsSocket } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  follow: "New follower",
  like: "New like",
  debate_reply: "New reply",
  group_invite: "Group invite",
  verification: "Verification",
};

export default function Notifications() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/notifications").then((r) => setNotes(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Live push via WebSocket — new notifications prepend without a refetch
  useNotificationsSocket((incoming) => {
    setNotes((prev) => {
      if (prev.find((n) => n.id === incoming.id)) return prev;
      return [incoming, ...prev];
    });
    toast(incoming.message, { description: KIND_META[incoming.kind] || "New activity" });
  });

  const readAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All marked as read");
    load();
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-8">
        <div>
          <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-1">Activity</div>
          <h1 className="font-serif text-4xl">Notifications</h1>
        </div>
        <Button variant="outline" onClick={readAll} className="rounded-xl text-xs font-semibold tracking-wider" data-testid="notif-read-all">
          <Check className="w-4 h-4 mr-2" /> Mark all read
        </Button>
      </div>

      {loading ? (
        <div className="text-xs font-semibold text-muted-foreground tracking-wider">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center text-xs font-semibold text-muted-foreground tracking-wider">
          <Bell className="w-8 h-8 mx-auto mb-3" /> No notifications yet
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {notes.map((n) => (
            <div
              key={n.id}
              className={`p-5 flex items-start gap-4 hover:bg-secondary transition-colors ${!n.read ? "bg-secondary/40" : ""}`}
              data-testid={`notif-${n.id}`}
            >
              <div className="w-2 h-2 mt-2 rounded-full bg-[hsl(var(--primary))] shrink-0" style={{ visibility: n.read ? "hidden" : "visible" }} />
              <div className="flex-1">
                <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-1">
                  {KIND_META[n.kind] || n.kind}
                </div>
                <p className="text-sm">
                  {n.message.split(/(@[a-z0-9_]+)/gi).map((chunk, i) =>
                    chunk.startsWith("@")
                      ? <a
                          key={i}
                          href={`/profile/${chunk.slice(1)}`}
                          className="text-[hsl(var(--primary))] font-semibold hover:underline"
                          data-testid={`notif-user-link-${chunk.slice(1)}`}
                        >{chunk}</a>
                      : <span key={i}>{chunk}</span>
                  )}
                </p>
                <div className="text-xs font-semibold text-muted-foreground tracking-wider mt-2">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
